<template>
  <!-- `?kit=1` mounts the shared-kit preview harness. `/` mounts this island, so the
       switch lives here. -->
  <HermesKitPreview v-if="showKit" />

  <div v-else class="h-home">
    <div class="h-home__content">
      <div>
        <h1 class="h-home__title">{{ greeting }}</h1>
        <p class="h-home__sub">Here's what needs you today.</p>
      </div>

      <HermesEmptyState
        v-if="error"
        variant="error"
        title="Couldn't load your home page"
        :message="error"
        @retry="load"
      />

      <HermesEmptyState v-else-if="loading" variant="loading" title="Loading" :skeleton-rows="5" />

      <template v-else>
        <section v-if="!setupDone" class="h-home__card">
          <div class="h-home__cardhead">
            <h2>Finish setting up</h2>
            <span class="h-home__muted">{{ stepsDone }} of {{ steps.length }} done</span>
          </div>
          <div class="h-home__progress" role="img" :aria-label="`${stepsDone} of ${steps.length} steps done`">
            <i :style="{ width: `${(stepsDone / steps.length) * 100}%` }"></i>
          </div>
          <div>
            <div v-for="(s, i) in steps" :key="s.key" class="h-home__step" :class="{ 'is-done': s.done }">
              <span class="h-home__tick" aria-hidden="true">{{ s.done ? '✓' : i + 1 }}</span>
              <div>
                <b>{{ s.title }}</b>
                <div class="h-home__help">{{ s.help }}</div>
              </div>
              <button v-if="!s.done && s.can" type="button" class="h-home__btn" @click="goTo(s.to)">Start</button>
            </div>
          </div>
        </section>

        <section class="h-home__card">
          <h2>Needs you</h2>
          <p v-if="!needs.length" class="h-home__help">Nothing right now.</p>
          <div v-else>
            <div v-for="n in needs" :key="n.key" class="h-home__row">
              <div>
                <b>{{ n.title }}</b>
                <div class="h-home__help">{{ n.help }}</div>
              </div>
              <button v-if="n.to" type="button" class="h-home__btn" @click="goTo(n.to)">{{ n.action || 'Look' }}</button>
            </div>
          </div>
        </section>

        <div class="h-home__stats">
          <HermesStatTile label="Taken this month" :value="takenThisMonth" :currency="currency" raw />
          <HermesStatTile label="Monthly income" :value="monthlyIncome" :currency="currency" raw />
          <HermesStatTile label="Paying customers" :value="counts.paying_customers" raw />
          <HermesStatTile label="Overdue" :value="counts.overdue" raw />
        </div>

        <section class="h-home__card">
          <div class="h-home__cardhead">
            <h2>Latest payments</h2>
            <router-link class="h-home__link" to="/admin/transactions">See all</router-link>
          </div>
          <p v-if="!payments.length" class="h-home__help">No payments yet.</p>
          <div v-else class="h-home__scroll">
            <table class="h-home__table">
              <thead>
                <tr><th>Customer</th><th>For</th><th>Status</th><th class="h-home__num">Amount</th></tr>
              </thead>
              <tbody>
                <tr v-for="p in payments" :key="p.id">
                  <td>
                    <b>{{ p.end_user_name || p.end_user_email || 'A customer' }}</b>
                    <div class="h-home__help">{{ paidWhen(p) }}</div>
                  </td>
                  <td>{{ p.description || '' }}</td>
                  <td><HermesStatusBadge :status="p.status" :role="statusRole(p.status)" :label="statusLabel(p.status)" /></td>
                  <td class="h-home__num"><HermesMoneyCell :amount="p.amount_total" :currency="p.currency" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import { dayAndDate, daysFromNow, friendlyWhen, shortDate } from '@root/lib/friendlyDate';

const STATUS_LABELS = {
  succeeded: 'Paid', failed: 'Failed', refunded: 'Refunded', partially_refunded: 'Part refunded',
  processing: 'Confirming', pending: 'Waiting', cancelled: 'Cancelled',
};

const PROVIDERS = { revolut: 'Revolut', stripe: 'Stripe', create_pay: 'Create Pay' };

// Home: the to-do page. Every line comes from a feed; a step or a person with nothing
// behind it does not appear.
export default {
  name: 'HermesDashboard',
  props: {
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      showKit: typeof window !== 'undefined' && /[?&]kit=1\b/.test(window.location.search),
      apiBase: this.componentData?.apiBase || '/api/v1',
      greetingName: this.componentData?.greetingName || '',
      loading: true,
      error: null,
      summary: {},
      home: { setup: {}, needs: [], counts: {}, can: {} },
      alerts: [],
      payments: [],
    };
  },
  computed: {
    greeting() {
      const h = new Date().getHours();
      const part = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
      return this.greetingName ? `${part}, ${this.greetingName}` : part;
    },
    counts() {
      return this.home.counts;
    },
    currency() {
      return this.summary.currency || '';
    },
    // One currency's figures: a sum across currencies means nothing.
    takenThisMonth() {
      return Number(this.summary.mtd_revenue_by_currency?.[this.currency] ?? 0);
    },
    monthlyIncome() {
      return Number(this.summary.mrr ?? 0);
    },
    steps() {
      const { setup, can } = this.home;
      const names = setup.product_names || [];
      const live = setup.mode === 'live';
      const steps = [
        {
          key: 'platform', done: setup.platform, can: can.manage_products, to: '/admin/catalogue',
          title: 'Add the platforms you sell through',
          help: setup.platform ? `${this.listNames(names)} ${names.length === 1 ? 'is' : 'are'} added.` : 'Add each product you sell.',
        },
        {
          key: 'connect', done: setup.connect, can: can.manage_gateways, to: '/admin/business#getting-paid',
          title: 'Connect how you get paid',
          help: 'Link Revolut or Stripe. Takes about two minutes.',
        },
        {
          key: 'prices', done: setup.prices, can: can.manage_plans, to: '/admin/catalogue',
          title: 'Set your prices',
          help: 'Add what you sell and what it costs.',
        },
        {
          key: 'first_payment', done: setup.first_payment, can: true, to: '/admin/payment-requests',
          title: live ? 'Take your first payment' : 'Send yourself a test payment',
          help: live ? 'Your account is live, so this one is real money.' : 'Ask yourself for a payment, then pay it with a test card.',
        },
        {
          key: 'app', done: setup.app_key, can: can.manage_api_keys, to: '/admin/business#your-app',
          title: 'Connect your app',
          help: 'Get the key your app uses to talk to Hermes.',
        },
      ];
      return steps.map((s) => (s.done || s.can ? s : { ...s, help: `${s.help} Ask whoever runs this account to do this.` }));
    },
    stepsDone() {
      return this.steps.filter((s) => s.done).length;
    },
    setupDone() {
      return this.stepsDone === this.steps.length;
    },
    needs() {
      const fromFeed = this.home.needs.map((n, i) => ({ key: `n${i}`, ...this.describe(n) }));
      const overLimit = this.alerts.map((a, i) => ({ key: `a${i}`, ...this.describeAlert(a) }));
      return [...fromFeed, ...overLimit];
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const [summary, home, transactions] = await Promise.all([
          this.api.get(`${this.apiBase}/admin/summary`),
          this.api.get(`${this.apiBase}/admin/home`),
          this.api.get(`${this.apiBase}/admin/transactions`),
        ]);
        this.summary = summary;
        this.home = home;
        this.payments = (transactions.items || []).slice(0, 5);
        this.loadAlerts();
      } catch (e) {
        const status = e?.response?.status;
        this.error = `Please try again.${status ? ` (${status})` : ''}`;
      } finally {
        this.loading = false;
      }
    },
    // Runs after the page shows: it walks every live subscription.
    async loadAlerts() {
      try {
        const data = await this.api.get(`${this.apiBase}/admin/enforcement-alerts`);
        this.alerts = data.items || [];
      } catch (e) {
        this.alerts = [];
      }
    },
    describe(n) {
      const to = n.subscription_id ? `/admin/subscriptions/${n.subscription_id}` : null;
      const what = [n.product, n.plan].filter(Boolean).join(' ');
      switch (n.type) {
        case 'refund_failed':
          return {
            to: '/admin/transactions',
            title: `A refund to ${n.customer} failed`,
            help: `${n.reason ? `${n.reason} ` : ''}The money has not gone back. Look at the payment and refund it again.`,
          };
        case 'overdue':
          return {
            to,
            title: `${n.customer}'s payment is overdue`,
            help: `${what ? `${what}. ` : ''}Hermes has stopped trying, so this one needs a person.`,
          };
        case 'retrying':
          return {
            to,
            title: `${n.customer}'s payment failed`,
            help: `${n.reason ? `${n.reason} ` : ''}${n.at ? `Hermes tries again on ${dayAndDate(n.at)}.` : 'Hermes will try again.'}`,
          };
        case 'trial_ending': {
          const days = daysFromNow(n.at);
          return {
            to,
            title: `${n.customer}'s trial ends in ${days} ${days === 1 ? 'day' : 'days'}`,
            help: n.charges
              ? `First charge on ${shortDate(n.at)}. Nothing to do unless you want to get in touch.`
              : `It ends on ${shortDate(n.at)} with no charge, as it was given by hand.`,
          };
        }
        case 'connection_unfinished': {
          const provider = PROVIDERS[n.gateway] || n.gateway;
          return {
            to: this.home.can.manage_gateways ? '/admin/business#getting-paid' : null,
            action: 'Finish',
            title: `Finish connecting ${provider}`,
            help: `Hermes can't hear back from ${provider} yet, so payments won't confirm.`,
          };
        }
        default:
          return { title: n.type, help: '' };
      }
    },
    describeAlert(a) {
      const who = a.customer_email || 'A customer';
      const plan = [a.product_name, a.plan_name].filter(Boolean).join(' ') || 'plan';
      const used = a.limit == null ? `${a.used}` : `${a.used} of ${a.limit}`;
      return {
        to: `/admin/subscriptions/${a.subscription_id}`,
        title: a.state === 'warning' ? `${who} is close to the limit on ${plan}` : `${who} is over the limit on ${plan}`,
        help: `${a.metric}: ${used} used.`,
      };
    },
    listNames(names) {
      if (names.length <= 2) return names.join(' and ');
      return `${names[0]}, ${names[1]} and ${names.length - 2} more`;
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status;
    },
    statusRole(status) {
      return status === 'partially_refunded' ? 'warning' : '';
    },
    paidWhen(p) {
      const at = Number(p.paid_at || p.created);
      return at ? friendlyWhen(at) : '';
    },
    goTo(path) {
      this.$router.push(path);
    },
  },
  mounted() {
    if (!this.showKit) this.load();
  },
};
</script>

<style scoped>
.h-home { font-family: var(--h-font-body, system-ui); color: var(--h-text-primary, #0F172A); background: var(--h-bg-content, #F8FAFC); min-height: 100vh; }
.h-home__content { max-width: 1040px; margin: 0 auto; padding: 26px 32px 64px; display: grid; gap: 20px; }
.h-home__content > * { min-width: 0; }
.h-home__title { font-size: 25px; font-weight: 700; letter-spacing: -0.02em; margin: 0; }
.h-home__sub { font-size: 15px; color: var(--h-text-secondary, #64748B); margin: 2px 0 0; }

.h-home__card { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); padding: 18px; display: grid; gap: 12px; min-width: 0; }
.h-home__card h2 { font-size: 16px; font-weight: 600; margin: 0; }
.h-home__cardhead { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 10px; }
.h-home__muted { font-size: 14px; color: var(--h-text-secondary, #64748B); }
.h-home__help { font-size: 13px; color: var(--h-text-secondary, #64748B); margin: 0; }

.h-home__progress { height: 6px; border-radius: 3px; background: var(--h-bg-subtle, #F1F5F9); overflow: hidden; }
.h-home__progress i { display: block; height: 100%; background: var(--h-success-text, #047857); }

.h-home__step { display: grid; grid-template-columns: 26px minmax(0, 1fr) auto; gap: 10px; align-items: center; padding: 10px 0; border-top: 1px solid var(--h-border, #E2E8F0); }
.h-home__step:first-of-type { border-top: 0; }
.h-home__step b { font-weight: 600; }
.h-home__step.is-done b { color: var(--h-text-secondary, #64748B); font-weight: 500; }
.h-home__tick { width: 24px; height: 24px; border-radius: 50%; border: 2px solid var(--h-border-strong, #CBD5E1); display: grid; place-items: center; font-size: 13px; font-weight: 700; color: var(--h-text-secondary, #64748B); }
.h-home__step.is-done .h-home__tick { background: var(--h-success-bg, #ECFDF5); border-color: var(--h-success-text, #047857); color: var(--h-success-text, #047857); }

.h-home__row { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; padding: 11px 0; border-top: 1px solid var(--h-border, #E2E8F0); }
.h-home__row:first-of-type { border-top: 0; }
.h-home__row > div { min-width: 0; flex: 1 1 240px; }
.h-home__row b { font-weight: 600; }

.h-home__btn { font: inherit; font-size: 14px; font-weight: 600; color: var(--h-accent-dark, #0369A1); background: none; border: 1px solid var(--h-border-strong, #CBD5E1); border-radius: var(--h-radius, 6px); padding: 7px 14px; cursor: pointer; }
.h-home__btn:hover { background: var(--h-bg-subtle, #F1F5F9); }
.h-home__btn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-home__link { font-size: 14px; font-weight: 500; color: var(--h-accent-dark, #0369A1); text-decoration: none; }
.h-home__link:hover { text-decoration: underline; }

.h-home__stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr)); gap: 12px; }

.h-home__scroll { overflow-x: auto; }
.h-home__table { width: 100%; min-width: 560px; border-collapse: collapse; font-size: 14px; }
.h-home__table th { text-align: left; font-size: 13px; font-weight: 500; color: var(--h-text-secondary, #64748B); padding: 6px 10px; }
.h-home__table td { padding: 11px 10px; border-top: 1px solid var(--h-border, #E2E8F0); vertical-align: middle; }
.h-home__table b { font-weight: 600; }
.h-home__table .h-home__num { text-align: right; }

@media (max-width: 720px) {
  .h-home__content { padding: 20px 16px 48px; }
}
</style>
